// src/api/features/leads/queries.ts
import {
  useQuery,
  type QueryClient,
  type QueryKey,
  keepPreviousData,
} from "@tanstack/react-query";

import { listLeads, getLead } from "./api";

import type {
  PaginatedLeads,
  LeadWithProducts,
  ListLeadsQuery,
} from "./types";

/* =========================
 * Query Keys
 * ========================= */

export const LeadsKeys = {
  all: ["leads"] as const,
  list: (params: ListLeadsQuery = {}) =>
    ["leads", "list", params] as const,
  detail: (id: number | string) =>
    ["leads", "detail", String(id)] as const,
} as const;

/* =========================
 * LEADS — React Query Hooks
 * ========================= */

/** 🔹 List leads with pagination + filters (list rows) */
export function useLeads(params: ListLeadsQuery = {}) {
  const key = LeadsKeys.list(params);

  return useQuery<PaginatedLeads>({
    queryKey: key,
    queryFn: () => listLeads(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });
}

/**
 * 🔹 Get a single lead (DEEP: with products + transfers)
 * - Returns LeadWithProducts
 */
export function useLead(id: number | string | null | undefined) {
  const enabled = id !== null && id !== undefined && id !== "";
  const detailKey: QueryKey = enabled
    ? LeadsKeys.detail(id as number | string)
    : (["leads", "detail", "disabled"] as QueryKey);

  return useQuery<LeadWithProducts>({
    queryKey: detailKey,
    queryFn: () => getLead(id as number | string),
    enabled,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
  });
}

/* =========================
 * Prefetch helpers (optional)
 * ========================= */

export function prefetchLeads(qc: QueryClient, params: ListLeadsQuery = {}) {
  return qc.prefetchQuery({
    queryKey: LeadsKeys.list(params),
    queryFn: () => listLeads(params),
    staleTime: 30_000,
  });
}

export function prefetchLead(qc: QueryClient, id: number | string) {
  return qc.prefetchQuery({
    queryKey: LeadsKeys.detail(id),
    queryFn: () => getLead(id),
    staleTime: 60_000,
  });
}
